/**
 * Centralized Appliance Rental Lease Template
 * 
 * SINGLE SOURCE OF TRUTH for all appliance rental lease agreements.
 * All lease-related functions should use this template.
 * 
 * Last Updated: January 24, 2026
 */

export type ApplianceType = 'washer_dryer' | 'washer' | 'dryer' | 'washer_dryer_plus' | 'washer_plus' | 'dryer_plus';

export interface ApplianceLeaseData {
   customerName: string;
   customerEmail: string;
   customerPhone: string;
   customerAddress: string;
   applianceType?: ApplianceType;
   equipmentDescription?: string;
   equipmentSerialNumber?: string;
   equipmentValue?: number;
   monthlyRent?: number;
   minimumTerm?: number;
   startDate: Date;
   endDate?: Date;
}

export function getDefaultEquipment(type: ApplianceType): { description: string; value: number; rent: number } {
  switch (type) {
    case 'washer':
      return { description: 'Top-Load Washing Machine', value: 400, rent: 25.00 };
    case 'dryer':
      return { description: 'Electric Dryer', value: 350, rent: 25.00 };
    case 'washer_dryer_plus':
      return { description: 'Washer & Dryer Set', value: 700, rent: 50.00 };
    case 'washer_plus':
      return { description: 'Top-Load Washing Machine', value: 400, rent: 30.00 };
    case 'dryer_plus':
      return { description: 'Electric Dryer', value: 350, rent: 30.00 };
    case 'washer_dryer':
    default:
      return { description: 'Washer & Dryer Set', value: 700, rent: 40.00 };
  }
}

/**
 * Generate the complete appliance rental lease agreement
 * This is the ONLY function that should be used to generate appliance leases
 */
export function generateApplianceLease(data: ApplianceLeaseData): string {
   const startDate = data.startDate.toLocaleDateString();
   const endDate = data.endDate
      ? data.endDate.toLocaleDateString()
      : new Date(data.startDate.getTime() + 365 * 24 * 60 * 60 * 1000).toLocaleDateString();

   const applianceType = data.applianceType || 'washer_dryer';
   const defaults = getDefaultEquipment(applianceType);
   const monthlyRent = data.monthlyRent || defaults.rent;
   const equipmentValue = data.equipmentValue || defaults.value;
   const minimumTerm = data.minimumTerm || 6;
   const equipment = data.equipmentDescription || defaults.description;
   const serial = data.equipmentSerialNumber || 'To be recorded at installation';
   const isCombo = applianceType.startsWith('washer_dryer');
   const isWasherOnly = applianceType === 'washer' || applianceType === 'washer_plus';
   const applianceLabel = isCombo ? 'washer and dryer units' : isWasherOnly ? 'washing machine' : 'dryer unit';

   return `
APPLIANCE RENTAL AGREEMENT

This Appliance Rental Agreement ("Agreement") is entered into as of ${startDate},
by and between NestlySuite Inc. ("Company" or "Lessor") and ${data.customerName} ("Lessee").

═══════════════════════════════════════════════════════════════════════════════
1. EQUIPMENT DESCRIPTION
═══════════════════════════════════════════════════════════════════════════════

The Company agrees to rent to the Lessee the following appliances:
• ${equipment}
• Serial Number: ${serial}

EQUIPMENT VALUE: The total value of the equipment including delivery and installation 
is ${equipmentValue.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}. 
This value represents the replacement cost and shall be used to calculate any damages 
or recovery amounts.

═══════════════════════════════════════════════════════════════════════════════
2. RENTAL TERM
═══════════════════════════════════════════════════════════════════════════════

Start Date: ${startDate}
End Date: ${endDate}
Minimum Term: ${minimumTerm} months

This agreement shall automatically renew for successive 6-month terms. Rent is charged 
monthly starting from the date the lease is signed until cancelled or terminated.

SHORT-TERM LEASE FEE: If Lessee elects a lease duration of less than ${minimumTerm} months, 
a Short-Term Lease Fee of $100.00 is due prior to installation, in addition to the 
first month's rent. Total Due at Installation for short-term leases: $${(monthlyRent + 100).toFixed(2)}.

═══════════════════════════════════════════════════════════════════════════════
3. RENTAL PAYMENTS & PAYMENT METHOD REQUIREMENTS
═══════════════════════════════════════════════════════════════════════════════

Monthly Rent: $${monthlyRent.toFixed(2)}
Due Date: Monthly on the same day as the lease signing.
First Payment: Due immediately upon lease signing.
Late Fee: $10.00 applies to payments received more than 5 days after the due date.

MANDATORY PAYMENT METHOD: Lessee MUST maintain a valid payment card (credit or debit) 
on file with the Company at all times during the rental term. Failure to maintain an 
active payment method is a material breach of this Agreement.

LATE PAYMENT GRACE PERIOD: If payment is not received within ten (10) days after the 
due date ("Grace Period"), this Agreement shall be subject to immediate termination 
and equipment repossession as outlined in Section 9.

═══════════════════════════════════════════════════════════════════════════════
4. DELIVERY & INSTALLATION
═══════════════════════════════════════════════════════════════════════════════

Equipment will be delivered and installed at:
${data.customerAddress}

Installation is included at no additional charge.

PHOTO DOCUMENTATION: Lessor will photograph equipment at installation. Lessee agrees 
these photos establish baseline condition for damage assessment purposes.

═══════════════════════════════════════════════════════════════════════════════
5. MAINTENANCE & CARE
═══════════════════════════════════════════════════════════════════════════════

• Lessee agrees to perform regular cleaning and maintenance
• Company will provide repairs for normal wear and tear
• Lessee is responsible for damages caused by misuse or negligence

═══════════════════════════════════════════════════════════════════════════════
6. 90-DAY STATUS CHECK REQUIREMENT (MANDATORY)
═══════════════════════════════════════════════════════════════════════════════

IMPORTANT: Lessee agrees to provide photographic evidence of appliance condition
every 90 days via the Customer Portal. This includes:

• Photo must clearly show the current condition of the ${applianceLabel}
• Lessee must perform a cleaning/maintenance check including:
${isWasherOnly ? `  - Clean detergent dispenser and drum
  - Wipe down exterior surfaces
  - Check for and report any unusual noises, leaks, or performance issues
  - Inspect hoses and connections for wear` : !isCombo ? `  - Clean lint trap and exhaust vent
  - Wipe down exterior surfaces
  - Check for and report any unusual noises or performance issues
  - Inspect vent connection for blockage or wear` : `  - Clean lint trap and exhaust vent on dryer
  - Clean detergent dispenser and drum on washer
  - Wipe down exterior surfaces
  - Check for and report any unusual noises, leaks, or performance issues
  - Inspect hoses and connections for wear`}

• Lessee will receive email reminders 7 days before each 90-day deadline
• Failure to submit the 90-day report may result in a technician visit at Lessee's expense

═══════════════════════════════════════════════════════════════════════════════
7. MANDATORY RENTER'S INSURANCE REQUIREMENT
═══════════════════════════════════════════════════════════════════════════════

a. INSURANCE REQUIREMENT: Lessee MUST maintain a valid Renter's Insurance policy 
   that includes coverage for personal property, liability, and water damage 
   throughout the ENTIRE term of this Agreement. This is a mandatory condition 
   of service.

b. CLAIMS RESPONSIBILITY: In the event of any damage to the rental premises, 
   Lessee's personal property, or third-party property (including but not limited 
   to water damage, flooding, electrical damage, or any other loss), Lessee 
   acknowledges and agrees that:
   - Lessee MUST file all claims with their own Renter's Insurance carrier
   - The Company (Lessor) shall NOT be liable for any such damages
   - Lessee shall look SOLELY to their Renter's Insurance for recovery

c. LIMITATION OF LIABILITY: Lessee acknowledges that the Company shall not be 
   liable for any damage to the rental premises (including but not limited to 
   water damage, flooring, drywall, or structural damage) or to Lessee's personal 
   property caused by the equipment, or for any losses resulting from equipment 
   failure, malfunction, or use.

d. INDEMNIFICATION: Lessee agrees to indemnify, defend, and hold harmless the 
   Company from any and all claims, damages, liabilities, costs, and expenses 
   (including reasonable attorney's fees) arising from the use or possession of 
   the equipment.

e. FAILURE TO MAINTAIN INSURANCE: Failure to maintain valid Renter's Insurance 
   constitutes a material breach of this Agreement and grounds for immediate 
   termination and equipment recovery.

═══════════════════════════════════════════════════════════════════════════════
8. TERMINATION
═══════════════════════════════════════════════════════════════════════════════

Either party may terminate this Agreement with 30 days written notice. Early 
termination by Lessee before the minimum ${minimumTerm}-month term incurs an Early Termination 
Fee of $100.00.

═══════════════════════════════════════════════════════════════════════════════
9. DEFAULT, REMEDIES & EQUIPMENT RECOVERY
═══════════════════════════════════════════════════════════════════════════════

a. EVENTS OF DEFAULT: The following constitute events of default:
   - Failure to pay rent within the 10-day Grace Period
   - Failure to maintain a valid payment card on file
   - Failure to maintain active Renter's Insurance
   - Any other material breach of this Agreement

b. IMMEDIATE TERMINATION: Upon default, Lessor may terminate this Agreement 
   immediately without further notice and demand return of the equipment.

c. EQUIPMENT PICKUP: Upon termination, Lessee agrees to:
   - Grant Lessor access to the premises to retrieve the equipment
   - Be present at the scheduled pickup date and time
   - Return the equipment in good working condition (normal wear excepted)

d. FAILURE TO GRANT ACCESS FOR PICKUP: If Lessee fails to grant access for 
   equipment pickup at the agreed-upon date and time, Lessee shall be liable for:
   - The FULL EQUIPMENT VALUE of ${equipmentValue.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
   - The Early Termination Fee of $100.00
   - ALL pending and unpaid rental fees
   - Reasonable collection costs and attorney's fees

e. 48-HOUR PAYMENT REQUIREMENT: Upon notification of amounts due under this 
   section, Lessee must make full payment within forty-eight (48) working hours.

f. FAILURE TO PAY & THEFT REPORTING: If Lessee fails to make payment within 
   the 48-hour period and refuses to return the equipment, the Company reserves 
   the right to:
   - Consider the equipment stolen rental property
   - Report the matter to local law enforcement authorities
   - Pursue all available civil and criminal remedies
   - Seek recovery of all damages, costs, and fees

g. THEFT BY CONVERSION WARNING: If Lessee fails to return the Rented Appliances 
   within seven (7) days of termination or demand for return, Lessee agrees and 
   acknowledges that such failure constitutes "THEFT BY CONVERSION" under Ohio 
   Revised Code Section 2913.02 (or equivalent applicable state law). Lessee 
   expressly acknowledges and agrees that:
   - Lessor WILL file a police report for theft of rental property
   - Lessor WILL press criminal charges to the fullest extent of the law
   - A criminal conviction may result in fines, restitution, and imprisonment
   - This acknowledgment serves as notice that Lessee has been warned of 
     the criminal consequences of non-return

h. AUTOMATIC PURCHASE UPON NON-RETURN: If Lessee refuses to return equipment 
   after contract termination and the 7-day return period, this Agreement 
   automatically converts to a SALE. Upon such conversion:
   - Lessee will be immediately invoiced the FULL REPLACEMENT VALUE (${equipmentValue.toLocaleString('en-US', { style: 'currency', currency: 'USD' })})
   - Title to the equipment shall NOT transfer until full payment is received
   - Lessee remains liable for all outstanding rent, late fees, and costs
   - The Company retains all rights to pursue criminal charges for theft
   - Payment of the equipment value does NOT absolve prior criminal conduct

═══════════════════════════════════════════════════════════════════════════════
10. CONSENT TO ELECTRONIC NOTICE & TRACKING
═══════════════════════════════════════════════════════════════════════════════

a. ELECTRONIC COMMUNICATIONS: Lessee explicitly and irrevocably consents to 
   receive ALL legal notices, including but not limited to notices of default, 
   termination demands, and demands for return of property, via:
   - The email address provided in this Agreement (${data.customerEmail})
   - The mobile phone number provided in this Agreement (${data.customerPhone}) via SMS/text

b. EMAIL TRACKING ACKNOWLEDGMENT: Lessee acknowledges that Lessor utilizes 
   electronic tracking technology (including read receipts and delivery 
   confirmation) for all legal correspondence. Lessee agrees that:
   - Opening an email constitutes LEGAL PROOF OF RECEIPT
   - Delivery confirmation to Lessee's email server constitutes valid service
   - Electronic records of delivery/opening are admissible as evidence

c. FAILURE TO RESPOND: Failure to reply to any electronic notice within 
   twenty-four (24) hours shall be deemed as:
   - Acknowledgment of the notice contents
   - Consent to any deadlines or requirements stated therein
   - Waiver of any objection to the notice or its terms

d. ADDRESS UPDATES: Lessee agrees to notify Lessor within 48 hours of any 
   change to email address or phone number. Notices sent to the last known 
   address shall be deemed delivered.

═══════════════════════════════════════════════════════════════════════════════
11. AMENDMENTS AND UPDATES TO TERMS
═══════════════════════════════════════════════════════════════════════════════

a. RIGHT TO AMEND: The Company (NestlySuite Inc.) reserves the right to modify, 
   update, or amend the terms of this Rental Agreement at any time to reflect 
   changes in business operations, legal requirements, or service improvements.

b. NOTIFICATION: Lessee will be notified of any material changes via the 
   email address on file or through the customer portal.

c. CONTINUED USE: Lessee agrees that their continued use of the equipment or 
   services, or payment of rent after such notice constitutes their full 
   acknowledgment and acceptance of the updated Agreement terms.

d. ACCEPTANCE REQUIREMENT: For material changes, Lessee may be required to 
   acknowledge and accept the updated terms through the customer portal 
   before continuing to access account services.

═══════════════════════════════════════════════════════════════════════════════
12. PURCHASE OPTION
═══════════════════════════════════════════════════════════════════════════════

Lessee shall have the option to purchase the Rented Appliances at any time for 
the price of ${equipmentValue.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}. This price is inclusive of the appliances, 
initial delivery, and installation costs. Upon payment in full, title to the 
appliances shall transfer to Lessee, and this rental agreement shall terminate.

═══════════════════════════════════════════════════════════════════════════════
13. GOVERNING LAW & SEVERABILITY
═══════════════════════════════════════════════════════════════════════════════

This Agreement shall be governed by and construed in accordance with the laws 
of the State of Ohio, without regard to conflicts of law principles. If any 
provision of this Agreement is held to be invalid, illegal, or unenforceable, 
the remaining provisions shall continue in full force and effect.

═══════════════════════════════════════════════════════════════════════════════
14. BINDING ARBITRATION
═══════════════════════════════════════════════════════════════════════════════

Any dispute, claim, or controversy arising out of or relating to this Agreement 
or the breach, termination, enforcement, interpretation, or validity thereof 
shall be determined by binding arbitration in the State of Ohio, administered 
by the American Arbitration Association under its Consumer Arbitration Rules. 
This clause precludes litigation in court or trial by jury.

═══════════════════════════════════════════════════════════════════════════════
15. APPLICATION REVIEW & DATA PROCESSING INDEMNIFICATION
═══════════════════════════════════════════════════════════════════════════════

a. REVIEW PROCESS DISCLOSURE: Lessee acknowledges that while no formal credit 
   check is performed, NestlySuite Inc. utilizes publicly available information
   —including but not limited to court records, criminal background data, and 
   social media—to evaluate eligibility for appliance rental services.

b. AI & AUTOMATED PROCESSING: NestlySuite Inc. uses Large Language Models (LLMs) 
   and Artificial Intelligence to assist in the processing and summarization of 
   application data. By signing this Agreement, Lessee consents to the automated 
   processing of their information for the purpose of risk assessment. All 
   automated decisions are subject to human review before any final determination.

c. VETTING INDEMNIFICATION: Lessee agrees to indemnify and hold NestlySuite Inc. 
   harmless from any liability, loss, or legal claims arising from the use of 
   public records or third-party data analysis (including AI tools) used during 
   the vetting process or throughout the duration of this Agreement.

d. CONSENT RECORD: Lessee's acceptance of these terms is recorded electronically 
   with a timestamp and shall serve as proof of informed consent for any legal 
   proceedings.

═══════════════════════════════════════════════════════════════════════════════
16. ENTIRE AGREEMENT
═══════════════════════════════════════════════════════════════════════════════

This Agreement constitutes the entire understanding between the parties and 
supersedes all prior agreements, representations, or understandings, whether 
written or oral. Lessee may not assign this Agreement without prior written 
consent from Lessor.

═══════════════════════════════════════════════════════════════════════════════
17. SIGNATURES
═══════════════════════════════════════════════════════════════════════════════

By signing below, both parties agree to the terms of this Agreement. Lessee 
acknowledges having read and understood all terms, including:

• The mandatory Renter's Insurance requirements
• Payment obligations and late fee policies
• Equipment recovery and repossession rights
• THEFT BY CONVERSION warnings and criminal consequences
• Electronic notice consent and tracking acknowledgment
• Amendments and updates to terms clause
• Application review, AI processing, and vetting indemnification

LESSOR (NESTLY SUITE INC.)
Signature: Godswill OGO                           Date: ${startDate}

LESSEE (CUSTOMER)
Signature: _____________________________          Date: ____________

═══════════════════════════════════════════════════════════════════════════════
© ${new Date().getFullYear()} NestlySuite Inc. All rights reserved.
support@nestlysuite.com | www.nestlysuite.com
═══════════════════════════════════════════════════════════════════════════════
`.trim();
}

/**
 * Generate the appliance lease as HTML for customer portal/email
 */
export function generateApplianceLeaseHTML(data: ApplianceLeaseData): string {
   const plainText = generateApplianceLease(data);

   // Convert plain text to HTML
   const htmlContent = plainText
      .replace(/═+/g, '<hr style="border: 2px solid #1e3a5f;">')
      .replace(/^(\d+\. [A-Z &]+)$/gm, '<h3 style="color: #1e3a5f; margin-top: 20px;">$1</h3>')
      .replace(/^([a-h]\. [A-Z ]+:)/gm, '<p style="margin: 10px 0;"><strong>$1</strong></p>')
      .replace(/• /g, '<br>• ')
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br>');

   return `
<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 800px; margin: 0 auto; padding: 20px; }
    h2 { text-align: center; color: #1e3a5f; }
    h3 { color: #1e3a5f; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
    hr { border: 2px solid #1e3a5f; margin: 20px 0; }
    .warning { background: #fff3cd; border: 1px solid #ffc107; padding: 10px; border-radius: 5px; }
    .critical { background: #f8d7da; border: 1px solid #dc3545; padding: 10px; border-radius: 5px; }
  </style>
</head>
<body>
  <h2>APPLIANCE RENTAL AGREEMENT</h2>
  <p>${htmlContent}</p>
</body>
</html>
`.trim();
}

export default {
   generateApplianceLease,
   generateApplianceLeaseHTML,
   getDefaultEquipment
};
